import type { Zone } from '@/lib/zones';

export type SwipeVector = {
  dx: number; // pixel delta from pointer-down to pointer-up
  dy: number; // negative is upward (toward goal)
  durationMs: number;
  startInGoal: boolean;
};

export type OffFrameReason = 'wide' | 'post' | 'no-power';

export type SwipeOutcome = {
  intendedZone: Zone | 'off'; // before noise (player's actual aim)
  actualZone: Zone | 'off';   // after noise (where ball lands)
  offFrameReason?: OffFrameReason;
};

const MIN_POWER_PX = 60;
// Above row: vertical component -dy must exceed this pixel threshold.
// Keeps high/low split in absolute terms so power-noise shifts rows correctly.
const MIN_ABOVE_DY_PX = 250;
// Column boundary: |dx/r| > this → Left/Right column (calibrated to test vectors).
const COLUMN_THRESHOLD = 0.24;
const NOISE_DIRECTION_DEG = 5;
const NOISE_POWER_PCT = 0.10;
const OFF_FRAME_FLOOR = 0.05;

const ALL_OFF_REASONS: readonly OffFrameReason[] = ['wide', 'post', 'no-power'];

function vectorToZone(vector: SwipeVector): Zone | 'off' {
  const { dx, dy } = vector;
  const r = Math.sqrt(dx * dx + dy * dy);
  if (r < MIN_POWER_PX) return 'off';
  // Need an upward component (toward goal). dy must be negative.
  if (dy >= 0) return 'off';

  const horizontal = dx / r;  // -1..1 (-1 = full left)

  // Row: high-kick (Above) vs low-kick (Front), based on absolute upward pixel distance.
  const row = -dy > MIN_ABOVE_DY_PX ? 'A' : 'F';

  let col: 'L' | 'C' | 'R';
  if (horizontal < -COLUMN_THRESHOLD) col = 'L';
  else if (horizontal > COLUMN_THRESHOLD) col = 'R';
  else col = 'C';

  return `${col}${row}` as Zone;
}

function applyNoise(
  intended: Zone | 'off',
  vector: SwipeVector,
  prng: () => number,
): SwipeOutcome {
  if (intended === 'off') {
    return { intendedZone: 'off', actualZone: 'off', offFrameReason: 'no-power' };
  }

  // 5% off-frame floor, applied first.
  if (prng() < OFF_FRAME_FLOOR) {
    const reasonIdx = Math.floor(prng() * ALL_OFF_REASONS.length);
    return {
      intendedZone: intended,
      actualZone: 'off',
      offFrameReason: ALL_OFF_REASONS[reasonIdx] ?? 'wide',
    };
  }

  // ±5° direction noise. We model jitter on the same vector by perturbing dx/dy
  // via rotation, then applying ±10% power scaling.
  const directionJitterRad = (prng() * 2 - 1) * (NOISE_DIRECTION_DEG * Math.PI) / 180;
  const powerJitter = 1 + (prng() * 2 - 1) * NOISE_POWER_PCT;

  const cosA = Math.cos(directionJitterRad);
  const sinA = Math.sin(directionJitterRad);
  const jitteredDx = (vector.dx * cosA - vector.dy * sinA) * powerJitter;
  const jitteredDy = (vector.dx * sinA + vector.dy * cosA) * powerJitter;

  const actual = vectorToZone({ ...vector, dx: jitteredDx, dy: jitteredDy });
  if (actual === 'off') {
    return {
      intendedZone: intended,
      actualZone: 'off',
      offFrameReason: 'no-power',
    };
  }
  return { intendedZone: intended, actualZone: actual };
}

/**
 * Map a swipe gesture (vector from pointer-down to pointer-up) onto a goal zone,
 * applying ±5° direction error + ±10% power error + 5% off-frame floor.
 * PRNG is injected for testability.
 */
export function aimToZone(
  vector: SwipeVector,
  prng: () => number,
): SwipeOutcome {
  const intended = vectorToZone(vector);
  return applyNoise(intended, vector, prng);
}
